import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { API_URL } from '../../config';
import { fetchEmployeeTaskStats, subscribeEmployeeTaskStats } from '../../utils/employeeTaskStats';
import styles from './EmployeeTaskStatsAvatar.module.css';

const COLOR_OVERDUE = '#C01048';
const COLOR_UNFINISHED = '#BD23DB';
const COLOR_COMPLETED = '#F4E2FF';

const RING_WIDTH = 2;
const RING_GAP = 8;
const RING_SUPERSAMPLE = 4;
const RING_VIEWBOX_PAD = RING_SUPERSAMPLE;

function getRingGeometry(size, ringWidth = RING_WIDTH) {
  const content = size * RING_SUPERSAMPLE;
  const stroke = ringWidth * RING_SUPERSAMPLE;
  const vb = content + RING_VIEWBOX_PAD * 2;
  const cx = vb / 2;
  const cy = vb / 2;
  const radius = (content - stroke) / 2;
  const circumference = 2 * Math.PI * radius;

  return { vb, stroke, cx, cy, radius, circumference };
}

function TaskStatsRing({ stats, size, ringWidth = RING_WIDTH }) {
  const { vb, stroke, cx, cy, radius } = getRingGeometry(size, ringWidth);

  const ringProps = {
    cx,
    cy,
    r: radius,
    fill: 'none',
    strokeWidth: stroke,
  };

  let rings;

  if (!stats) {
    rings = (
      <circle
        {...ringProps}
        stroke={COLOR_COMPLETED}
      />
    );
  } else if (stats.has_overdue) {
    rings = (
      <circle
        {...ringProps}
        stroke={COLOR_OVERDUE}
      />
    );
  } else {
    const unfinished = Math.max(0, Math.min(100, Number(stats.unfinished_percent) || 0));

    if (unfinished <= 0) {
      rings = (
        <circle
          {...ringProps}
          stroke={COLOR_COMPLETED}
        />
      );
    } else if (unfinished >= 100) {
      rings = (
        <circle
          {...ringProps}
          stroke={COLOR_UNFINISHED}
        />
      );
    } else {
      const completed = 100 - unfinished;
      const completedFraction = completed / 100;

      rings = (
        <>
          <circle
            {...ringProps}
            stroke={COLOR_UNFINISHED}
          />
          {(() => {
            const startAngle = -Math.PI / 2;
            const endAngle = startAngle - completedFraction * 2 * Math.PI;

            const x1 = cx + radius * Math.cos(startAngle);
            const y1 = cy + radius * Math.sin(startAngle);
            const x2 = cx + radius * Math.cos(endAngle);
            const y2 = cy + radius * Math.sin(endAngle);

            const largeArcFlag = completedFraction >= 0.5 ? 1 : 0;
            const sweepFlag = 0;

            const d = `M ${x1} ${y1} A ${radius} ${radius} 0 ${largeArcFlag} ${sweepFlag} ${x2} ${y2}`;

            return (
              <path
                d={d}
                fill="none"
                stroke={COLOR_COMPLETED}
                strokeWidth={stroke}
                strokeLinecap="round"
              />
            );
          })()}
        </>
      );
    }
  }

  return (
    <svg
      className={styles.ringSvg}
      width={size}
      height={size}
      viewBox={`0 0 ${vb} ${vb}`}
      overflow="visible"
      aria-hidden="true"
    >
      {rings}
    </svg>
  );
}

const API_ORIGIN = (() => {
  try {
    return (API_URL || '').replace(/\/api\/v\d+\/?$/i, '');
  } catch {
    return '';
  }
})();

function resolveAvatar(url) {
  if (!url) return null;
  if (url.startsWith('/uploads/')) return `${API_ORIGIN}${url}`;
  return url;
}

function buildAvatarCandidates(thumb, avatar) {
  const out = [];
  for (const url of [thumb, avatar]) {
    const resolved = resolveAvatar(url);
    if (resolved && !out.includes(resolved)) out.push(resolved);
  }
  return out;
}

function getInitialsFromName(name) {
  if (!name?.trim()) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return parts[0].slice(0, 2).toUpperCase();
}

export function EmployeeTaskStatsAvatar({
  employeeId,
  stats: statsProp,
  thumb,
  avatar,
  size = 48,
  className = '',
  onClick,
  alt = '',
  title,
  fetchStats = true,
  initials,
}) {
  const [fetchedStats, setFetchedStats] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    if (statsProp || !fetchStats || !employeeId) return undefined;

    const id = Number(employeeId);
    return subscribeEmployeeTaskStats((updatedIds) => {
      if (updatedIds.includes(id)) {
        setRefreshTick((tick) => tick + 1);
      }
    });
  }, [employeeId, statsProp, fetchStats]);

  useEffect(() => {
    if (statsProp || !fetchStats || !employeeId) return undefined;

    let cancelled = false;

    fetchEmployeeTaskStats([employeeId]).then((map) => {
      if (!cancelled) {
        setFetchedStats(map.get(Number(employeeId)) || null);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [employeeId, statsProp, fetchStats, refreshTick]);

  const stats = statsProp || fetchedStats;

  const wrapStyle = useMemo(
    () => ({
      width: size,
      height: size,
      '--avatar-gap': `${RING_GAP}px`,
      '--avatar-scale': size >= 72 ? 1.02 : 1,
    }),
    [size]
  );

  const candidates = useMemo(
    () => buildAvatarCandidates(thumb, avatar),
    [thumb, avatar]
  );
  const [idx, setIdx] = useState(0);
  const [useFallback, setUseFallback] = useState(() => candidates.length === 0);

  useEffect(() => {
    setIdx(0);
    setUseFallback(candidates.length === 0);
  }, [candidates]);

  const handleError = useCallback(() => {
    setIdx((i) => {
      if (i + 1 < candidates.length) return i + 1;
      setUseFallback(true);
      return i;
    });
  }, [candidates.length]);

  const displayInitials = initials || getInitialsFromName(alt);
  const fallbackFontSize = Math.max(11, Math.round(size * 0.34));

  const wrapClassName = [
    styles.wrap,
    onClick ? styles.clickable : '',
    className,
  ].filter(Boolean).join(' ');

  const content = (
    <>
      <div className={styles.inner}>
        {useFallback ? (
          <div className={styles.fallback} style={{ fontSize: fallbackFontSize }}>
            {displayInitials}
          </div>
        ) : (
          <img
            className={styles.img}
            src={candidates[idx]}
            alt={alt}
            onError={handleError}
          />
        )}
      </div>
      <TaskStatsRing stats={stats} size={size} ringWidth={RING_WIDTH} />
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        className={wrapClassName}
        style={wrapStyle}
        onClick={onClick}
        title={title}
        aria-label={alt || title}
      >
        {content}
      </button>
    );
  }

  return (
    <div className={wrapClassName} style={wrapStyle} title={title}>
      {content}
    </div>
  );
}
